import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp, UserButton } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { WorkspaceProvider, useWorkspace } from "@/lib/api";
import Layout from "@/components/app/Layout";
import Dashboard from "@/pages/Dashboard";
import Feeding from "@/pages/Feeding";
import Livestock from "@/pages/Livestock";
import Tasks from "@/pages/Tasks";
import Finances from "@/pages/Finances";
import Reporting from "@/pages/Reporting";
import Inspections from "@/pages/Inspections";
import Machinery from "@/pages/Machinery";
import Compliance from "@/pages/Compliance";
import Settings from "@/pages/Settings";
import StaffCard from "@/pages/StaffCard";
import CalendarPage from "@/pages/Calendar";
import PasturePage from "@/pages/Pasture";
import Grazing from "@/pages/Grazing";
import GrazierPortal from "@/pages/GrazierPortal";

const LOGIN_BG = "/login-farm.jpg";

const AuthShell = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-white overflow-x-hidden">
    <div className="hidden lg:flex relative flex-col justify-between p-12 text-white bg-[#1f2a1c] bg-cover bg-center" style={{ backgroundImage: `linear-gradient(180deg, rgba(20,32,18,.55) 0%, rgba(20,32,18,.35) 45%, rgba(20,32,18,.85) 100%), url(${LOGIN_BG})` }}>
      <p className="font-display text-xl font-bold tracking-tight" data-testid="tcg-brand">TCG Agriculture</p>
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-emerald-200">Farm Operations</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight leading-tight" data-testid="login-theme">Our farms.<br />Our people.<br /><span className="text-emerald-300">Our future.</span></h1>
        <p className="mt-5 text-emerald-50/90 max-w-md">Good work on the land deserves to be seen. Plan the day, record what gets done, and share the wins with the team you're building it with.</p>
      </div>
      <p className="text-xs text-emerald-100/70">Access is by invitation from TCG Agriculture.</p>
    </div>
    <div className="flex flex-col items-center justify-center p-6 bg-[#fbfaf6]">
      <div className="lg:hidden text-center mb-6"><p className="font-display text-xl font-bold">TCG Agriculture · Farm Operations</p><p className="text-sm text-emerald-800 mt-1" data-testid="login-theme-mobile">Our farms. Our people. Our future.</p></div>
      <p className="hidden lg:block mb-6 text-slate-600">Welcome back. Sign in to see today on your farm.</p>
      {children}
    </div>
  </div>
);

function Pending() {
  const { user, error } = useWorkspace();
  return (
    <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
      <div className="panel p-8 max-w-md" data-testid="pending-access">
        <div className="flex justify-between items-start mb-4"><p className="font-display font-bold">TCG Agriculture</p><UserButton /></div>
        <h1 className="text-2xl font-bold mb-2">{error ? "Couldn't load your workspace" : "Awaiting access"}</h1>
        <p className="text-sm text-slate-600">{error || `You're signed in as ${user?.email}. An administrator needs to assign your role and farms before you can see any farm data.`}</p>
      </div>
    </div>
  );
}

function Workspace() {
  const { me, error } = useWorkspace();
  if (!me && !error) return <div className="min-h-screen grid place-items-center text-sm text-slate-500" data-testid="loading">Loading workspace…</div>;
  if (error || me.user.role === "pending") return <Pending />;
  if (me.user.role === "grazier") return <GrazierPortal />;
  return (
    <Routes>
      <Route path="feeding/staff-card" element={<StaffCard />} />
      <Route element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="pasture" element={<PasturePage />} />
        <Route path="feeding" element={<Feeding />} />
        <Route path="livestock" element={<Livestock />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="finances" element={<Finances />} />
        <Route path="reporting" element={<Reporting />} />
        <Route path="inspections" element={<Inspections />} />
        <Route path="machinery" element={<Machinery />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="grazing" element={<Grazing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthShell><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthShell>} />
          <Route path="/sign-up/*" element={<AuthShell><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthShell>} />
          <Route path="/*" element={<>
            <Show when="signed-in"><WorkspaceProvider><Workspace /></WorkspaceProvider></Show>
            <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
          </>} />
        </Routes>
        <Toaster richColors position="top-right" />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
